import { Box, CardMedia, Skeleton, SxProps, Theme } from '@mui/material';

import BankIcon from 'modules/common/components/Icons/BankIcon';
import usePlaidInstitutionById from 'services/plaid/queries/usePlaidInstitutionById';
import insLogoMapping from 'modules/common/components/Icons/BankLogos/insLogoMapping';

interface BankLogoProps {
    institutionId?: string;
    color?: string | undefined;
    backgroundColor?: string | undefined;
    width?: number;
    height?: number;
    sx?: SxProps<Theme>;
    logo?: string | null;
}

export default function BankLogo({
    institutionId = '',
    color = 'cement.700',
    backgroundColor = 'cement.100',
    width = 48,
    height = 48,
    logo = null,
    ...rest
}: BankLogoProps) {
    const { data, isLoading } = usePlaidInstitutionById(institutionId, {
        enabled: !logo && !!institutionId,
    });

    if (institutionId && isLoading) {
        return <Skeleton variant="circular" width={width} height={height} />;
    }

    if (institutionId.length > 0 && insLogoMapping[institutionId]) {
        const { logo: Logo } = insLogoMapping[institutionId];
        // Use size based on width prop to match the container size
        let logoSize: 'sm' | 'md' | 'lg' = 'sm';
        if (width >= 48) {
            logoSize = width >= 64 ? 'lg' : 'md';
        }
        return (
            <Box sx={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Logo size={logoSize} />
            </Box>
        );
    }

    if (logo || data?.logo) {
        return (
            <CardMedia
                component="img"
                image={`data:image/jpeg;base64,${logo ?? data?.logo}`}
                title={data?.name}
                sx={{ width, height, borderRadius: '50%' }}
            />
        );
    }

    return (
        <Box width={width} height={height}>
            <BankIcon
                sx={{
                    backgroundColor: data?.primary_color ?? backgroundColor,
                    width,
                    height,
                    color: data?.primary_color ? 'white' : color,
                    p: 1, // Using theme spacing instead of hardcoded
                    borderRadius: '50%', // Keep circular shape
                    ...rest.sx,
                }}
            />
        </Box>
    );
}
